import { Classes, Menu, MenuItem, Popover, TagInput } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { filterProjectItemPredicate, optionLabel } from "modules/common/components/form/ProjectSelect"
import { Search } from "modules/common/components/form/Search"
import { DefaultSelectedOption, filterItemPredicate } from "modules/common/services/form/select"
import { texts } from "modules/common/texts"
import { OrderDictionaryItemType, OrderDictionaryType } from "modules/orders-manage/models/order-dictionary"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import React, { useCallback, useMemo, useRef, useState } from "react"
import styles from "./BoardFilters.module.scss"

/**
 * Фильтры шапки доски. Фильтра по статусам больше нет — статусы стали колонками.
 * observer нужен справочникам: они догружаются после первой отрисовки доски
 */
export const BoardFilters: React.FC<BoardFiltersProps> = observer(({
  orders,
  employers,
  hideProject,
  members,
  query,
  orderIds,
  authorIds,
  employerIds,
  implementerIds,
  setQuery,
  setOrderIds,
  setAuthorIds,
  setEmployerIds,
  setImplementerIds,
  rightTools,
  compact,
  children,
}) => {
  /** Узкая шапка виджета: селекты открываются отдельной строкой по кнопке */
  const [expanded, setExpanded] = useState(false)
  // на личной доске в фильтрах только её участники: чужих заданий там всё равно нет.
  // Список пересобираем копией: Blueprint обновляет выпадающий список только по новой ссылке,
  // а справочник — массив MST, он меняется на месте
  const employee = employers?.employee
  const people = useMemo(
    () => {
      const all = employee ? employee.slice() : []
      return members ? all.filter((e) => members.some((m) => m.id === e.id)) : all
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employee, employee?.length, members]
  )

  const selects = (
    <>
      {orders && !hideProject && <ProjectFilter projects={orders} selected={orderIds} onChange={setOrderIds} />}

      <PersonFilter what="Инициатор" many="Инициаторов" items={people} selected={authorIds} onChange={setAuthorIds} />
      <PersonFilter
        what="Ответственный"
        many="Ответственных"
        items={people}
        selected={employerIds}
        onChange={setEmployerIds}
      />
      <PersonFilter
        what="Исполнитель"
        many="Исполнителей"
        items={people}
        selected={implementerIds}
        onChange={setImplementerIds}
      />
    </>
  )

  // сколько фильтров сейчас отбирают задания: в свёрнутом виде их не видно
  const active = [orderIds, authorIds, employerIds, implementerIds].filter((ids) => ids.length > 0).length

  return (
    <div className={`${styles.filters} ${compact ? styles.filtersCompact : ""}`}>
      <div className={styles.tools}>{children}</div>

      <div className={styles.selects}>
        {compact ? (
          <PlanrButton
            type={active > 0 || expanded ? "blueish" : "graybtn"}
            size="small"
            title="Фильтры"
            onClick={() => setExpanded(!expanded)}
          >
            {active > 0 ? `Фильтры: ${active}` : "Фильтры"}
          </PlanrButton>
        ) : (
          selects
        )}

        <Search query={query} onSearch={setQuery} />

        <div className={styles.rightTools}>{rightTools}</div>
      </div>

      {/* селекты узкой доски: в одну строку с поиском и кнопками они не помещаются.
          Строка остаётся в разметке и в закрытом виде — иначе её нечем анимировать */}
      {compact && (
        <div className={`${styles.compactSelects} ${expanded ? styles.compactSelectsOpen : ""}`}>{selects}</div>
      )}
    </div>
  )
})

/**
 * Фильтр с мультивыбором: поле выглядит как обычный селект, но по клику вместо
 * выпадающего списка открывается список с отметками, где значений отмечают сколько нужно.
 * Список рисуем в потоке, а не выпадающим: иначе поповер не растёт под его высоту
 */
const MultiFilter: React.FC<MultiFilterProps> = ({
  what,
  many,
  placeholder,
  popoverClassName,
  items,
  selected,
  label,
  tag,
  match,
  onChange,
}) => {
  const [query, setQuery] = useState("")
  const input = useRef<HTMLInputElement | null>(null)

  const chosen = selected.map((id) => items.find((item) => item.id === id)).filter(Boolean) as FilterItem[]

  const filtered = useMemo(
    () => (query ? items.filter((item) => match(query, item)) : items.slice()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, items.length, query, match]
  )

  const toggle = useCallback(
    (item: FilterItem) => {
      // после отметки строку поиска сбрасываем: следующее значение ищут с чистого листа
      setQuery("")
      onChange(selected.includes(item.id) ? selected.filter((id) => id !== item.id) : [...selected, item.id])

      // отмечать значения подряд удобно с клавиатуры: курсор возвращаем в поиск
      input.current && input.current.focus()
    },
    [selected, onChange]
  )

  // крестик гасит всплытие сам, поповер от очистки не открывается
  const clear = useCallback(() => onChange([]), [onChange])

  // поле показывает одно значение названием, несколько — количеством
  const option =
    chosen.length === 0
      ? null
      : chosen.length === 1
      ? { id: chosen[0].id, label: label(chosen[0]) }
      : { id: "", label: `${many}: ${chosen.length}` }

  return (
    <div
      className={`${styles.filter} ${chosen.length > 0 ? styles.filterActive : ""}`}
      title={chosen.length > 1 ? chosen.map(label).join("\n") : undefined}
    >
      <Popover
        fill={true}
        minimal={true}
        rootBoundary="viewport"
        position="bottom-left"
        // те же классы, что навешивает SimpleSelect: поле выглядит как соседние фильтры
        className={`select ${Classes.FILL} planr-default-input`}
        popoverClassName={popoverClassName}
        onClosed={() => setQuery("")}
        content={
          <div className={styles.multiList}>
            <TagInput
              className={styles.multiTags}
              fill={true}
              values={chosen.map((item) => (
                <span key={label(item)} title={label(item)}>
                  {tag(item)}
                </span>
              ))}
              onRemove={(_value: any, index: number) => onChange(selected.filter((_, i) => i !== index))}
              inputValue={query}
              onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.currentTarget.value)}
              inputProps={{ autoFocus: true, placeholder }}
              inputRef={(el: HTMLInputElement | null) => (input.current = el)}
              rightElement={
                chosen.length > 0 ? (
                  <GeneralIcon
                    type="general-cross-small"
                    onClick={clear}
                    title="Очистить"
                    style={{ color: "#E31818", cursor: "pointer", alignSelf: "center" }}
                  />
                ) : undefined
              }
            />

            {/* клик по пункту не должен уводить фокус из поиска */}
            <Menu className={styles.multiMenu} onMouseDown={(e: React.MouseEvent) => e.preventDefault()}>
              {filtered.length === 0 && <MenuItem disabled={true} text={texts.noData} />}

              {filtered.map((item) => (
                <MenuItem
                  key={item.id}
                  icon={selected.includes(item.id) ? "tick" : "blank"}
                  text={label(item)}
                  // список остаётся открытым: значения отмечают подряд
                  shouldDismissPopover={false}
                  onClick={() => toggle(item)}
                />
              ))}
            </Menu>
          </div>
        }
      >
        <DefaultSelectedOption option={option as any} onClear={clear} what={what} />
      </Popover>
    </div>
  )
}

/** Фильтр по объектам: в теге номер договора, названия объектов длинные */
const ProjectFilter: React.FC<ProjectFilterProps> = observer(({ projects, selected, onChange }) => {
  // копия по той же причине, что и у сотрудников: справочник — массив MST, он меняется на месте
  const orders = projects.orders
  const items = useMemo(
    () => orders.slice(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, orders.length]
  )

  return (
    <MultiFilter
      what="Объект"
      many="Объектов"
      placeholder="Поиск объекта"
      popoverClassName={styles.projectPopover}
      items={items}
      selected={selected}
      label={(item) => optionLabel(item as OrderDictionaryItemType)}
      tag={(item) => (item as OrderDictionaryItemType).fullInventoryNumber || item.label}
      match={matchProject}
      onChange={onChange}
    />
  )
})

/** Фильтр по сотрудникам: в теге фамилия с инициалами, полное имя — подсказкой */
const PersonFilter: React.FC<PersonFilterProps> = ({ what, many, items, selected, onChange }) => (
  <MultiFilter
    what={what}
    many={many}
    placeholder="Поиск сотрудника"
    popoverClassName={styles.personPopover}
    items={items}
    selected={selected}
    label={(item) => item.label}
    tag={(item) => shortName(item.label)}
    match={matchPerson}
    onChange={onChange}
  />
)

const matchProject = (query: string, item: FilterItem) =>
  filterProjectItemPredicate(query, item as OrderDictionaryItemType, 0, false) as boolean

const matchPerson = (query: string, item: FilterItem) => filterItemPredicate(query, item, 0, false) as boolean

/** «Иванов Иван Иванович» → «Иванов И.И.»: тегов в поле несколько, места мало */
const shortName = (label: string) => {
  const [family, ...rest] = label.split(" ").filter(Boolean)

  return rest.length > 0 ? `${family} ${rest.map((word) => word[0].toUpperCase() + ".").join("")}` : label
}

/** Общее у пунктов любого фильтра: идентификатор и подпись */
interface FilterItem {
  id: string
  label: string
  [key: string]: any
}

interface MultiFilterProps {
  /** Название фильтра: подпись пустого поля */
  what: string
  /** Родительный падеж множественного числа: «Объектов: 3» */
  many: string
  placeholder: string
  popoverClassName: string
  items: FilterItem[]
  /** Идентификаторы выбранных значений */
  selected: string[]
  label: (item: FilterItem) => string
  /** Короткая подпись для тега в поле выбора */
  tag: (item: FilterItem) => string
  match: (query: string, item: FilterItem) => boolean
  onChange: (ids: string[]) => void
}

interface ProjectFilterProps {
  projects: OrderDictionaryType
  selected: string[]
  onChange: (ids: string[]) => void
}

interface PersonFilterProps {
  what: string
  many: string
  items: FilterItem[]
  selected: string[]
  onChange: (ids: string[]) => void
}

interface BoardFiltersProps {
  orders?: OrderDictionaryType
  employers?: EmployerDictionaryType
  hideProject?: boolean
  /** Участники личной доски: владелец и приглашённые */
  members?: { id: string; name: string }[] | null
  query: string
  /** Выбранные объекты: фильтр допускает несколько */
  orderIds: string[]
  /** Выбранные инициаторы, ответственные и исполнители: их тоже может быть несколько */
  authorIds: string[]
  employerIds: string[]
  implementerIds: string[]
  setQuery: (value: string) => void
  setOrderIds: (ids: string[]) => void
  setAuthorIds: (ids: string[]) => void
  setEmployerIds: (ids: string[]) => void
  setImplementerIds: (ids: string[]) => void
  /** Кнопки в правой части шапки, после фильтров */
  rightTools?: React.ReactNode
  /** Узкая шапка виджета главной: селекты скрыты за кнопкой «Фильтры» */
  compact?: boolean
  children?: React.ReactNode;
}
